import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { 
  ArrowLeft, Crown, Check, Sparkles, Infinity, 
  Wind, Book, Palette, Zap, Loader2 
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { celebrateStreakMilestone } from '../utils/confetti';
import { trackPremiumPageViewed, trackSubscriptionStarted } from '../lib/analytics';
import { isNativePlatform } from '../lib/native';
import { useCheckoutPrice } from '../lib/price';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Premium = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, setUserData } = useAuth();
  const [loading, setLoading] = useState(false);
  const [checkingPayment, setCheckingPayment] = useState(false);
  const priceLabel = useCheckoutPrice();

  // Defence in depth: if a native (iOS/Android) user ever reaches this
  // Stripe-driven page (deep link, browser back, JS navigation), bounce
  // them straight to Settings where NativeSubscribeButton (RevenueCat)
  // is the only compliant upgrade path. App.js /premium Route already
  // guards this at the router level; this covers direct <Premium />
  // renders and race conditions during platform detection.
  useEffect(() => {
    if (isNativePlatform()) {
      navigate('/settings', { replace: true });
    }
  }, [navigate]);

  const features = [
    {
      icon: <Infinity className="w-6 h-6" strokeWidth={1.5} />,
      title: "Unlimited Goals",
      description: "Track multiple transformations simultaneously"
    },
    {
      icon: <Sparkles className="w-6 h-6" strokeWidth={1.5} />,
      title: "AI Affirmations",
      description: "Personalized affirmations powered by Gemini AI"
    },
    {
      icon: <Wind className="w-6 h-6" strokeWidth={1.5} />,
      title: "Breathwork",
      description: "Guided breathing exercises to centre your mind"
    },
    {
      icon: <Book className="w-6 h-6" strokeWidth={1.5} />,
      title: "Private Journal",
      description: "Capture reflections on your transformation journey"
    },
    {
      icon: <Palette className="w-6 h-6" strokeWidth={1.5} />,
      title: "Custom Visualizations",
      description: "Create your own visualisation scripts"
    },
  ];

  // Poll payment status if returning from Stripe
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      pollPaymentStatus(sessionId);
    }
  }, [searchParams]);

  // Fire premium_page_viewed once on mount (no PII)
  useEffect(() => {
    trackPremiumPageViewed({ is_premium: !!user?.is_premium });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      toast.error('Payment status check timed out. Please contact support.');
      setCheckingPayment(false);
      return;
    }

    setCheckingPayment(true);

    try {
      const response = await fetch(`${API_URL}/api/payments/status/${sessionId}`, {
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Failed to check payment status');
      }

      const data = await response.json();

      if (data.payment_status === 'paid') {
        setUserData({ ...user, is_premium: true });
        celebrateStreakMilestone(30); // Luxurious gold confetti!
        toast.success('Welcome to Premium! 🎉');
        // Fire the approved event (no PII)
        trackSubscriptionStarted({ plan: 'monthly', method: 'stripe' });
        setCheckingPayment(false);
        // Clear the session_id from URL
        navigate('/premium', { replace: true });
        return;
      } else if (data.status === 'expired') {
        toast.error('Payment session expired. Please try again.');
        setCheckingPayment(false);
        navigate('/premium', { replace: true });
        return;
      }

      // Continue polling if still pending
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      toast.error('Error checking payment. Please try again.');
      setCheckingPayment(false);
    }
  };

  const handleUpgrade = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/payments/create-checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          origin_url: window.location.origin
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorMsg = errorData.detail || `Server error (${response.status})`;
        toast.error(errorMsg);
        setLoading(false);
        return;
      }

      const data = await response.json();

      // Redirect to Stripe Checkout
      if (data.url) {
        window.location.href = data.url;
      } else {
        toast.error('No checkout URL received. Please try again.');
        setLoading(false);
      }
    } catch (error) {
      toast.error('Could not connect to payment service. Please check your connection and try again.');
      setLoading(false);
    }
  };

  if (checkingPayment) {
    return (
      <div className="min-h-screen bg-[#1C1C1E] flex flex-col items-center justify-center px-6">
        <div className="w-16 h-16 rounded-full bg-[#C6A75E]/20 flex items-center justify-center mb-6 animate-pulse">
          <Loader2 className="w-8 h-8 text-[#C6A75E] animate-spin" strokeWidth={1.5} />
        </div>
        <h2 className="font-serif text-2xl text-[#DCD3CA] mb-2">Processing Payment...</h2>
        <p className="text-[#B5B1AB] font-sans">Please wait while we confirm your upgrade</p>
      </div>
    );
  }

  if (user?.is_premium) {
    return (
      <div className="min-h-screen bg-[#1C1C1E] flex flex-col items-center justify-center px-6">
        <div className="w-20 h-20 rounded-full bg-[#C6A75E]/20 flex items-center justify-center mb-6">
          <Crown className="w-10 h-10 text-[#C6A75E]" strokeWidth={1.5} />
        </div>
        <h1 className="font-serif text-3xl text-[#DCD3CA] mb-4 text-center">You're Premium!</h1>
        <p className="text-[#B5B1AB] text-center mb-8 font-sans">
          You have access to all premium features.
        </p>
        <Button
          onClick={() => navigate('/dashboard')}
          className="bg-[#8BAA9E] hover:bg-[#A3C2B6] text-[#1C1C1E] rounded-full px-8 py-6"
          data-testid="back-to-dashboard-btn"
        >
          Back to Dashboard
        </Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#1C1C1E]" data-testid="premium-page">
      {/* Header */}
      <header className="px-6 pt-8 pb-6">
        <button
          onClick={() => navigate(-1)}
          className="w-10 h-10 rounded-full bg-[#252528] flex items-center justify-center text-[#B5B1AB] hover:text-[#DCD3CA]"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-5 h-5" strokeWidth={1.5} />
        </button>
      </header>

      <div className="px-6 pb-8">
        {/* Hero */}
        <div className="text-center mb-10">
          <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[#C6A75E]/30 to-[#C6A75E]/10 flex items-center justify-center mx-auto mb-6 animate-pulse">
            <Crown className="w-10 h-10 text-[#C6A75E]" strokeWidth={1.5} />
          </div>
          <h1 className="font-serif text-4xl text-[#DCD3CA] mb-4">Go Premium</h1>
          <p className="text-[#B5B1AB] font-sans font-light max-w-sm mx-auto">
            Unlock the full potential of your transformation journey
          </p>
        </div>

        {/* Features */}
        <div className="space-y-4 mb-10">
          {features.map((feature) => (
            <div 
              key={feature.title}
              className="flex items-start gap-4 p-5 bg-[#252528] border border-white/5 rounded-[20px]"
            >
              <div className="w-12 h-12 rounded-full bg-[#C6A75E]/10 flex items-center justify-center text-[#C6A75E] flex-shrink-0">
                {feature.icon}
              </div>
              <div>
                <h3 className="font-serif text-lg text-[#DCD3CA] mb-1">{feature.title}</h3>
                <p className="text-[#B5B1AB] text-sm font-sans">{feature.description}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Pricing Card */}
        <div className="bg-gradient-to-br from-[#C6A75E]/20 to-[#C6A75E]/5 border border-[#C6A75E]/30 rounded-[24px] p-6 mb-6">
          <div className="flex items-baseline justify-center gap-1 mb-4">
            {priceLabel ? (
              <span className="font-serif text-5xl text-[#DCD3CA]" data-testid="premium-price">{priceLabel}</span>
            ) : (
              <span className="inline-block w-28 h-12 bg-white/5 rounded animate-pulse" data-testid="premium-price-loading" />
            )}
            <span className="text-[#B5B1AB] font-sans">/month</span>
          </div>
          <ul className="space-y-3 mb-6">
            {['Unlimited goals', 'AI-powered affirmations', 'Priority support', 'Cancel anytime'].map((item) => (
              <li key={item} className="flex items-center gap-3 text-[#DCD3CA] font-sans">
                <Check className="w-5 h-5 text-[#C6A75E]" />
                {item}
              </li>
            ))}
          </ul>
          <Button
            onClick={handleUpgrade}
            disabled={loading}
            className="w-full bg-[#C6A75E] hover:bg-[#D4B86E] text-[#1C1C1E] rounded-full py-6 text-base font-medium flex items-center justify-center gap-2"
            data-testid="upgrade-btn"
          >
            {loading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <>
                <Zap className="w-5 h-5" />
                Upgrade to Premium
              </>
            )}
          </Button>
        </div>

        {/* Free Tier Note */}
        <div className="text-center">
          <p className="text-[#7A7670] text-sm font-sans mb-2">Free tier includes:</p>
          <p className="text-[#B5B1AB] text-sm font-sans">1 active goal • Basic tracking • Visualization mode</p>
        </div>
      </div>
    </div>
  );
};

export default Premium;
